/**
 * Lógica pura del menú lateral agrupado por flujos: detección del grupo activo y
 * persistencia (por usuario) de qué grupos están abiertos. Sin React ni Fluent.
 */

export interface NavGroupLike {
  id: string;
  items: { to: string }[];
}

/** Estado inicial cuando no hay nada guardado: todos los grupos colapsados (se abre solo el de la ruta activa). */
export const DEFAULT_OPEN_GROUPS: Record<string, boolean> = {};

/** Evento para abrir todos los grupos (p. ej. durante el tour guiado) y volver al estado normal. */
export const NAV_EXPAND_ALL_EVENT = 'agrovid:nav-expand-all';
export const NAV_RESTORE_EVENT = 'agrovid:nav-restore';

/**
 * Grupo que contiene la ruta activa. `pathname` es `/<tenant>/<ruta>`; gana la coincidencia
 * más específica (`super-admin/config` antes que `super-admin`) y solo cuenta por segmentos
 * completos (`campos` no coincide con `campo`).
 */
export function findActiveGroupId(pathname: string, tenantId: string, groups: NavGroupLike[]): string | null {
  const prefix = `/${tenantId}/`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : null;
  if (rest === null) return null;
  let best: { id: string; len: number } | null = null;
  for (const g of groups) {
    for (const { to } of g.items) {
      if ((rest === to || rest.startsWith(`${to}/`)) && (!best || to.length > best.len)) {
        best = { id: g.id, len: to.length };
      }
    }
  }
  return best?.id ?? null;
}

const key = (userKey: string) => `agrovid:nav-groups:${userKey}`;

function isOpenMap(v: unknown): v is Record<string, boolean> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
    && Object.values(v as Record<string, unknown>).every((x) => typeof x === 'boolean');
}

/** Todo en try/catch: localStorage puede no existir o estar bloqueado. */
export function loadOpenGroups(userKey: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(key(userKey));
    if (!raw) return { ...DEFAULT_OPEN_GROUPS };
    const parsed: unknown = JSON.parse(raw);
    return isOpenMap(parsed) ? parsed : { ...DEFAULT_OPEN_GROUPS };
  } catch {
    return { ...DEFAULT_OPEN_GROUPS };
  }
}

export function saveOpenGroups(userKey: string, open: Record<string, boolean>): void {
  try { localStorage.setItem(key(userKey), JSON.stringify(open)); } catch { /* sin storage: se ignora */ }
}
